'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import { MAGIC_LINK_ORIGIN_LOCAL_STORAGE_KEY } from '../magicLinks/constants';
import {
  getLocalStorageItem,
  getSessionStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  setSessionStorageItem,
} from '../storage';

import { LAST_ROUTE_SESSION_STORAGE_KEY } from './constants';
import { useCurrentRoute } from './useCurrentRoute';

/**
 * Matched per path segment rather than anchored, because the pathname still
 * carries its locale prefix here (/gb/login/ as well as /login/).
 */
const AUTH_PATH_SEGMENTS = /(^|\/)(login|signup|magic-link|password-reset)\//;

export function useRecordLastRoute() {
  const pathname = usePathname();
  const currentRoute = useCurrentRoute();

  useEffect(() => {
    if (!pathname || !currentRoute || AUTH_PATH_SEGMENTS.test(pathname)) {
      return;
    }

    setSessionStorageItem(LAST_ROUTE_SESSION_STORAGE_KEY, currentRoute);
  }, [pathname, currentRoute]);
}

export function getLastRoute() {
  return getSessionStorageItem(LAST_ROUTE_SESSION_STORAGE_KEY);
}

/**
 * Carries the origin across browsing contexts, for flows that resume in a tab
 * that has no session storage of its own — a magic link opened from an email
 * client. Write on every request so a previous, abandoned one cannot leak into
 * the next signup.
 */
export function persistPageOrigin(origin?: string | null) {
  if (!origin) {
    removeLocalStorageItem(MAGIC_LINK_ORIGIN_LOCAL_STORAGE_KEY);
    return;
  }

  setLocalStorageItem(MAGIC_LINK_ORIGIN_LOCAL_STORAGE_KEY, origin);
}

export function clearPersistedPageOrigin() {
  removeLocalStorageItem(MAGIC_LINK_ORIGIN_LOCAL_STORAGE_KEY);
}

export function getPageOrigin() {
  return (
    getLastRoute() ||
    getLocalStorageItem(MAGIC_LINK_ORIGIN_LOCAL_STORAGE_KEY) ||
    null
  );
}
